import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const cancelDispatchResponseSchema = z.object({
	success: z.boolean(),
	carrierDispatchCancelled: z.boolean(),
	confirmationNumber: z.string().nullable(),
	carrierRejectionMessage: z.string().nullable(),
});

export type CancelDispatchResponse = z.infer<
	typeof cancelDispatchResponseSchema
>;

// The API flags a failed cancel against this customer; the shipment page only
// shows flags for the shipment's customer, so omitting it hides the exception.
export type CancelDispatchRequest = {
	poNumber: number;
	customerId?: number | null;
};

export const postCancelDispatch = async ({
	poNumber,
	customerId,
}: CancelDispatchRequest) => {
	const resp = await postAndValidate(
		apiRoutes.POSTCANCELDISPATCH,
		{ poNumber, customerId },
		cancelDispatchResponseSchema,
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostCancelDispatch = () => {
	const queryClient = useQueryClient();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		CancelDispatchResponse,
		Error,
		CancelDispatchRequest
	>({
		mutationKey: ["cancelDispatch"],
		mutationFn: postCancelDispatch,
		onSuccess: () => {
			// Return the invalidation promise so the mutation stays pending (and
			// the spinner stays visible) until the shipment query has refetched.
			// This guarantees the shipment progress reflects the cancelled dispatch
			// before the success modal opens.
			return queryClient.invalidateQueries({ queryKey: ["shipment"] });
		},
	});

	return {
		cancelDispatch: mutate,
		cancelDispatchAsync: mutateAsync,
		isSavingCancelDispatch: isPending,
		error,
		data,
		resetError: reset,
	};
};
